import { useTranslation } from 'react-i18next';
import { Check, X, Pencil } from 'lucide-react';

/**
 * Single milestone card. Label is inline-editable; editing state and storage
 * are owned by the parent so multiple cards can coordinate (only one
 * editable at a time).
 */
export function MilestoneCard({
  index,
  milestone,
  isEditing,
  editValue,
  setEditValue,
  onStartEdit,
  onSave,
  onCancel,
}) {
  const { t } = useTranslation();
  const Icon = milestone.icon;

  return (
    <div
      className="p-4 rounded-lg border border-[#DDE1E5] bg-white flex items-start gap-3 group"
      data-testid={`milestone-${index}`}
    >
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${milestone.color}15` }}
      >
        <Icon className="w-4 h-4" style={{ color: milestone.color }} />
      </div>
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <div className="flex items-center gap-1">
            <input
              autoFocus
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') onSave();
                if (e.key === 'Escape') onCancel();
              }}
              className="text-xs font-semibold text-[#171A1D] bg-[#F5F6F7] border border-[#527F19] rounded px-2 py-0.5 w-full focus:outline-none"
              data-testid={`milestone-edit-input-${index}`}
            />
            <button
              onClick={onSave}
              className="text-[#78B928] hover:text-green-600 shrink-0"
              data-testid={`milestone-save-btn-${index}`}
            >
              <Check className="w-3 h-3" />
            </button>
            <button
              onClick={onCancel}
              className="text-[#8A929B] hover:text-[#66707A] shrink-0"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <span className="text-xs font-semibold text-[#171A1D] truncate">{milestone.label}</span>
            <button
              onClick={onStartEdit}
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity shrink-0 ml-1 text-[#8A929B] hover:text-[#66707A] focus-visible:ring-1 focus-visible:ring-[#78B928] rounded"
              data-testid={`milestone-edit-btn-${index}`}
              aria-label={`${t('gantt.editLabel')}: ${milestone.label}`}
              title={t('gantt.editLabel')}
            >
              <Pencil className="w-2.5 h-2.5" />
            </button>
          </div>
        )}
        <div
          className="text-sm font-semibold text-[#171A1D] mt-0.5"
          style={{ fontFamily: 'IBM Plex Mono, monospace' }}
        >
          {milestone.weekLabel}
        </div>
      </div>
    </div>
  );
}
